/**
 * Structural Column Tool - BIM 2025 Grade
 * 
 * Vertical structural elements with full complexity:
 * - Slanted/inclined columns (two-point placement)
 * - Base/Top level constraints with offsets
 * - Top attachment (beam, floor, nothing)
 * - Base attachment (foundation, floor, beam)
 * - Multiple profiles (Rectangular, Round, I-Shape)
 * - Splice locations
 * - Fireproofing specification
 */

import { v4 as uuidv4 } from 'uuid';
import { Element, StructuralMaterial, AnalyticalModel, RebarHostData, Constraint } from '../core/types';
import { BaseTool } from './BaseTool';
import { ThreeEvent } from '@react-three/fiber';
import * as THREE from 'three';

// ==================== Column Types ====================

export type ColumnStyle = 'Vertical' | 'Slanted';

export type ColumnPlacement =
    | 'AtGridIntersection'
    | 'AlongGrid'
    | 'Free';

export type AttachmentType =
    | 'None'
    | 'Beam'
    | 'Floor'
    | 'Foundation'
    | 'Roof'
    | 'Wall';

// ==================== Column Profile ====================

export interface ColumnProfile {
    shape: 'Rectangular' | 'Round' | 'I-Shape' | 'L-Shape' | 'T-Shape' | 'Cruciform' | 'Hollow';

    // Rectangular/I-Shape/Hollow
    width: number;          // mm (X direction)
    depth: number;          // mm (Y direction)

    // Round
    diameter?: number;      // mm

    // I-Shape specific
    flangeWidth?: number;
    flangeThickness?: number;
    webThickness?: number;

    // Hollow
    wallThickness?: number;
}

// ==================== Splice ====================

export interface ColumnSplice {
    enabled: boolean;
    heightFromBase: number;     // mm
    spliceType: 'Bolted' | 'Welded' | 'Mechanical Coupler';
    plateThickness?: number;    // mm
}

// ==================== Fireproofing ====================

export interface ColumnFireproofing {
    type: 'None' | 'Spray-on' | 'Board' | 'Concrete Encasement';
    thickness: number;          // mm
    fireRating: string;         // e.g., "2 Hour"
}

// ==================== Enhanced Geometry ====================

export interface EnhancedColumnGeometryData {
    // Position - vertical or slanted
    basePoint: [number, number, number];
    topPoint: [number, number, number];

    // Style
    style: ColumnStyle;

    // Profile
    profile: ColumnProfile;
    rotation: number;           // degrees around Z-axis

    // Level constraints
    baseLevelId: string;
    topLevelId: string;
    baseOffset: number;         // mm
    topOffset: number;          // mm

    // Attachments
    baseAttachment: {
        type: AttachmentType;
        elementId?: string;
        cutColumn: boolean;     // Cut column at attachment
    };
    topAttachment: {
        type: AttachmentType;
        elementId?: string;
        cutColumn: boolean;
    };

    // Splice
    splice?: ColumnSplice;

    // Fireproofing
    fireproofing: ColumnFireproofing;

    // Calculated
    height: number;             // m
    isSlanged: boolean;
    angle: number;              // degrees from vertical
}

// ==================== Enhanced Column Element ====================

export interface EnhancedColumnElement extends Element {
    category: 'Structural Columns';
    geometryData: EnhancedColumnGeometryData;

    // Structural data
    structuralMaterial: StructuralMaterial;
    analyticalModel: AnalyticalModel;
    rebarHostData: RebarHostData;

    // Connections
    connections: {
        base?: { type: string; connectedElementIds: string[] };
        top?: { type: string; connectedElementIds: string[] };
    };

    // Fabrication
    fabrication?: {
        mark: string;
        pieceNumber: number;
        weight: number;         // kg
    };
}

// ==================== Tool Options ====================

export interface EnhancedColumnToolOptions {
    // Type
    familyName: string;
    typeName: string;
    profile: ColumnProfile;

    // Style
    style: ColumnStyle;
    placement: ColumnPlacement;

    // Level constraints
    baseLevelId: string;
    topLevelId: string;
    baseOffset: number;
    topOffset: number;
    rotation: number;

    // Material
    materialId: string;

    // Attachments
    attachTopToElement: boolean;
    attachBaseToElement: boolean;

    // Splice
    splice?: ColumnSplice;

    // Fireproofing
    fireproofing: ColumnFireproofing;
}

// Standard column profiles
export const STANDARD_COLUMN_PROFILES: Record<string, ColumnProfile> = {
    'M_Concrete-Rectangular-Column: 450 x 600mm': {
        shape: 'Rectangular',
        width: 450,
        depth: 600
    },
    'M_Concrete-Rectangular-Column: 300 x 300mm': {
        shape: 'Rectangular',
        width: 300,
        depth: 300
    },
    'M_Concrete-Round-Column: 600mm Diameter': {
        shape: 'Round',
        width: 600,
        depth: 600,
        diameter: 600
    },
    'M_Concrete-Round-Column: 450mm Diameter': {
        shape: 'Round',
        width: 450,
        depth: 450,
        diameter: 450
    },
    'W-Wide Flange-Column: W310x97': {
        shape: 'I-Shape',
        width: 305,
        depth: 308,
        flangeWidth: 305,
        flangeThickness: 15.4,
        webThickness: 9.9
    },
    'W-Wide Flange-Column: W360x134': {
        shape: 'I-Shape',
        width: 369,
        depth: 356,
        flangeWidth: 369,
        flangeThickness: 18.0,
        webThickness: 11.2
    },
    'HSS-Hollow Structural Section-Column: HSS152.4x152.4x9.5': {
        shape: 'Hollow',
        width: 152.4,
        depth: 152.4,
        wallThickness: 9.5
    },
    'Cruciform-Column: 400x400mm': {
        shape: 'Cruciform',
        width: 400,
        depth: 400
    }
};

// ==================== Column Tool Class ====================

export class ColumnTool extends BaseTool {
    id = 'column';
    name = 'Column';
    cursor = 'crosshair';

    // For slanted column placement
    private basePoint: THREE.Vector3 | null = null;
    private currentPoint: THREE.Vector3 | null = null;

    private options: EnhancedColumnToolOptions = this.getDefaultOptions();

    private getDefaultOptions(): EnhancedColumnToolOptions {
        return {
            familyName: 'M_Concrete-Rectangular-Column',
            typeName: '450 x 600mm',
            profile: STANDARD_COLUMN_PROFILES['M_Concrete-Rectangular-Column: 450 x 600mm'],
            style: 'Vertical',
            placement: 'Free',
            baseLevelId: 'Level 1',
            topLevelId: 'Level 2',
            baseOffset: 0,
            topOffset: 0,
            rotation: 0,
            materialId: 'mat-concrete-40',
            attachTopToElement: false,
            attachBaseToElement: false,
            fireproofing: {
                type: 'None',
                thickness: 0,
                fireRating: ''
            }
        };
    }

    /**
     * Set column options from UI
     */
    setOptions(options: Partial<EnhancedColumnToolOptions>): void {
        this.options = { ...this.options, ...options };
    }

    /**
     * Sync placement/style from the shared toolProperties so the Ribbon
     * "At Grids" toggle drives real placement behavior (BIM CL > At Grids).
     */
    activate(context: Parameters<BaseTool['activate']>[0]): void {
        super.activate(context);
        const props = context.bimStore.toolProperties;
        if (props.columnPlacementMode === 'AtGrids') {
            this.options.placement = 'AtGridIntersection';
        } else if (props.columnPlacementMode === 'Free') {
            this.options.placement = 'Free';
        }
        if (props.columnStyle) {
            this.options.style = props.columnStyle;
        }
    }

    /**
     * Get current options
     */
    getOptions(): EnhancedColumnToolOptions {
        return { ...this.options };
    }

    onPointerDown(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        if (event.button !== 0) return;

        const point = this.getIntersection(event);
        if (!point) return;

        let snapped = this.snapToGrid(point);

        // Snap to grid intersection if placement mode
        if (this.options.placement === 'AtGridIntersection') {
            snapped = this.snapToGridIntersection(snapped) || snapped;
        }

        if (this.options.style === 'Slanted') {
            // Two-point placement for slanted columns
            if (!this.basePoint) {
                this.basePoint = snapped;
                this.currentPoint = snapped;
            } else {
                // Create slanted column
                const column = this.createColumn(this.basePoint, snapped);
                this.context.bimStore.addElement(column);

                // Detect and set attachments
                this.detectAttachments(column);

                this.basePoint = null;
                this.currentPoint = null;
            }
        } else {
            // Single-point placement for vertical columns
            const column = this.createVerticalColumn(snapped);
            this.context.bimStore.addElement(column);

            // Detect and set attachments
            this.detectAttachments(column);
        }
    }

    onPointerMove(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;

        const point = this.getIntersection(event);
        if (point) {
            let snapped = this.snapToGrid(point);

            if (this.options.placement === 'AtGridIntersection') {
                snapped = this.snapToGridIntersection(snapped) || snapped;
            }

            this.currentPoint = snapped;
            this.updatePreview(snapped);
        }
    }

    onPointerUp(): void { }

    onKeyDown(event: KeyboardEvent): void {
        if (event.key === 'Escape') {
            if (this.basePoint) {
                this.basePoint = null;
                this.currentPoint = null;
                this.context?.appStore.setToolPreview(null);
            } else {
                super.onKeyDown(event);
            }
        }
    }

    private snapToGridIntersection(point: THREE.Vector3): THREE.Vector3 | null {
        if (!this.context) return null;

        const tolerance = 0.5; // 500mm
        let closest: THREE.Vector3 | null = null;
        let closestDist = tolerance;

        // Find grid intersections
        const grids = Array.from(this.context.bimStore.project.grids.values());

        for (let i = 0; i < grids.length; i++) {
            for (let j = i + 1; j < grids.length; j++) {
                const intersection = this.getGridIntersection(grids[i], grids[j]);
                if (intersection) {
                    const dist = point.distanceTo(new THREE.Vector3(intersection.x, intersection.y, point.z));
                    if (dist < closestDist) {
                        closestDist = dist;
                        closest = new THREE.Vector3(intersection.x, intersection.y, point.z);
                    }
                }
            }
        }

        return closest;
    }

    private getGridIntersection(grid1: any, grid2: any): { x: number; y: number } | null {
        // Line-line intersection
        const p1 = { x: grid1.start[0], y: grid1.start[1] };
        const p2 = { x: grid1.end[0], y: grid1.end[1] };
        const p3 = { x: grid2.start[0], y: grid2.start[1] };
        const p4 = { x: grid2.end[0], y: grid2.end[1] };

        const d = (p1.x - p2.x) * (p3.y - p4.y) - (p1.y - p2.y) * (p3.x - p4.x);
        if (Math.abs(d) < 0.001) return null; // Parallel

        const t = ((p1.x - p3.x) * (p3.y - p4.y) - (p1.y - p3.y) * (p3.x - p4.x)) / d;

        return {
            x: p1.x + t * (p2.x - p1.x),
            y: p1.y + t * (p2.y - p1.y)
        };
    }

    private updatePreview(snapped: THREE.Vector3): void {
        if (!this.context) return;

        const profile = this.options.profile;

        if (this.options.style === 'Slanted' && this.basePoint) {
            // Show preview for slanted column
            this.context.appStore.setToolPreview({
                type: 'column',
                style: 'Slanted',
                basePoint: this.basePoint,
                topPoint: snapped,
                width: profile.width / 1000,
                depth: profile.depth / 1000,
                rotation: this.options.rotation
            });
        } else {
            // Show preview for vertical column
            this.context.appStore.setToolPreview({
                type: 'column',
                style: 'Vertical',
                position: snapped,
                width: profile.width / 1000,
                depth: profile.depth / 1000,
                height: 3, // Default preview height
                rotation: this.options.rotation
            });
        }
    }

    private createVerticalColumn(point: THREE.Vector3): EnhancedColumnElement {
        // Get level elevations
        const baseElevation = this.getLevelElevation(this.options.baseLevelId);
        const topElevation = this.getLevelElevation(this.options.topLevelId);

        const baseZ = baseElevation + (this.options.baseOffset / 1000);
        const topZ = topElevation + (this.options.topOffset / 1000);

        const basePoint = new THREE.Vector3(point.x, point.y, baseZ);
        const topPoint = new THREE.Vector3(point.x, point.y, topZ);

        return this.createColumn(basePoint, topPoint);
    }

    public getLevelElevation(levelId: string): number {
        if (!this.context) return 0;
        const level = this.context.bimStore.project.levels.get(levelId);
        return level ? level.elevation / 1000 : 0; // Convert mm to m
    }

    private createColumn(basePoint: THREE.Vector3, topPoint: THREE.Vector3): EnhancedColumnElement {
        const height = basePoint.distanceTo(topPoint);
        const isSlanted = Math.abs(basePoint.x - topPoint.x) > 0.01 ||
            Math.abs(basePoint.y - topPoint.y) > 0.01;

        // Calculate angle from vertical
        const vertical = new THREE.Vector3(0, 0, 1);
        const columnDir = topPoint.clone().sub(basePoint).normalize();
        const angle = Math.acos(Math.abs(columnDir.dot(vertical))) * (180 / Math.PI);

        const geometryData: EnhancedColumnGeometryData = {
            basePoint: basePoint.toArray() as [number, number, number],
            topPoint: topPoint.toArray() as [number, number, number],
            style: isSlanted ? 'Slanted' : 'Vertical',
            profile: { ...this.options.profile },
            rotation: this.options.rotation,
            baseLevelId: this.options.baseLevelId,
            topLevelId: this.options.topLevelId,
            baseOffset: this.options.baseOffset,
            topOffset: this.options.topOffset,
            baseAttachment: {
                type: 'None',
                cutColumn: false
            },
            topAttachment: {
                type: 'None',
                cutColumn: false
            },
            splice: this.options.splice,
            fireproofing: { ...this.options.fireproofing },
            height,
            isSlanged: isSlanted,
            angle
        };

        // Create analytical model
        const analyticalModel = this.createAnalyticalModel(basePoint, topPoint);

        // Get material
        const structuralMaterial = this.getMaterial(this.options.materialId);

        // Rebar hosting
        const rebarHostData = this.getRebarHostData();

        // Calculate weight
        const weight = this.calculateWeight(geometryData, structuralMaterial);

        const column: EnhancedColumnElement = {
            id: uuidv4(),
            category: 'Structural Columns',
            type: `${this.options.familyName}: ${this.options.typeName}`,
            parameters: this.buildParameters(geometryData, weight),
            constraints: this.buildConstraints(),
            geometryData,
            structuralMaterial,
            analyticalModel,
            rebarHostData,
            connections: {},
            fabrication: {
                mark: '',
                pieceNumber: 0,
                weight
            }
        };

        return column;
    }

    private createAnalyticalModel(basePoint: THREE.Vector3, topPoint: THREE.Vector3): AnalyticalModel {
        const baseNode = {
            id: uuidv4(),
            position: basePoint.toArray() as [number, number, number],
            boundaryConditions: 'Fixed' as const // Columns typically fixed at base
        };

        const topNode = {
            id: uuidv4(),
            position: topPoint.toArray() as [number, number, number],
            boundaryConditions: 'Pinned' as const // Beam-column connections typically pinned
        };

        return {
            nodes: [baseNode, topNode],
            curves: [{
                startNodeId: baseNode.id,
                endNodeId: topNode.id,
                type: 'Column' as const
            }]
        };
    }

    private getMaterial(materialId: string): StructuralMaterial {
        const materials: Record<string, StructuralMaterial> = {
            'mat-concrete-40': {
                id: 'mat-concrete-40',
                name: 'Concrete - C40/50',
                type: 'Concrete',
                properties: {
                    youngsModulus: 35000,
                    shearModulus: 14500,
                    poissonRatio: 0.2,
                    density: 2400,
                    thermalExpansionCoefficient: 0.00001,
                    concreteCompression: 40
                }
            },
            'mat-concrete-50': {
                id: 'mat-concrete-50',
                name: 'Concrete - C50/60',
                type: 'Concrete',
                properties: {
                    youngsModulus: 38000,
                    shearModulus: 15800,
                    poissonRatio: 0.2,
                    density: 2400,
                    thermalExpansionCoefficient: 0.00001,
                    concreteCompression: 50
                }
            },
            'mat-steel-350': {
                id: 'mat-steel-350',
                name: 'Steel - S350',
                type: 'Steel',
                properties: {
                    youngsModulus: 210000,
                    shearModulus: 81000,
                    poissonRatio: 0.3,
                    density: 7850,
                    thermalExpansionCoefficient: 0.000012,
                    yieldStrength: 350
                }
            }
        };

        return materials[materialId] || materials['mat-concrete-40'];
    }

    private getRebarHostData(): RebarHostData {
        const isConcrete = this.options.materialId.includes('concrete');

        return {
            validHost: isConcrete,
            coverSettings: {
                top: isConcrete ? 40 : 0,
                bottom: isConcrete ? 40 : 0,
                sides: isConcrete ? 40 : 0
            }
        };
    }

    private calculateWeight(geom: EnhancedColumnGeometryData, material: StructuralMaterial): number {
        const profile = geom.profile;
        let area = 0; // m²

        switch (profile.shape) {
            case 'Rectangular':
                area = (profile.width / 1000) * (profile.depth / 1000);
                break;
            case 'Round':
                area = Math.PI * Math.pow((profile.diameter || profile.width) / 2000, 2);
                break;
            case 'I-Shape': {
                const flangeArea = 2 * (profile.flangeWidth! / 1000) * (profile.flangeThickness! / 1000);
                const webArea = ((profile.depth! - 2 * profile.flangeThickness!) / 1000) * (profile.webThickness! / 1000);
                area = flangeArea + webArea;
                break;
            }
            case 'Hollow': {
                const outer = (profile.width / 1000) * (profile.depth / 1000);
                const inner = ((profile.width - 2 * profile.wallThickness!) / 1000) *
                    ((profile.depth - 2 * profile.wallThickness!) / 1000);
                area = outer - inner;
                break;
            }
            case 'Cruciform': {
                // Approximate as two intersecting rectangles
                const armThickness = profile.width / 4;
                area = 2 * (profile.width / 1000) * (armThickness / 1000) -
                    (armThickness / 1000) * (armThickness / 1000);
                break;
            }
            default:
                area = (profile.width / 1000) * (profile.depth / 1000);
        }

        const volume = area * geom.height;
        return volume * material.properties.density;
    }

    private buildParameters(geom: EnhancedColumnGeometryData, weight: number): Record<string, any> {
        const profile = geom.profile;

        return {
            // Dimensions
            'b': profile.width,
            'h': profile.depth,
            'Height': geom.height,

            // Placement
            'Style': geom.style,
            'Base Level': geom.baseLevelId,
            'Top Level': geom.topLevelId,
            'Base Offset': geom.baseOffset,
            'Top Offset': geom.topOffset,
            'Column Rotation': geom.rotation,

            // Slant
            'Is Slanted': geom.isSlanged,
            'Angle From Vertical': geom.angle,

            // Attachments
            'Top Attachment': geom.topAttachment.type,
            'Base Attachment': geom.baseAttachment.type,

            // Fireproofing
            'Fireproofing': geom.fireproofing.type,
            'Fire Rating': geom.fireproofing.fireRating,

            // Calculated
            'Volume': geom.height * (profile.width / 1000) * (profile.depth / 1000),
            'Weight': weight
        };
    }

    protected buildConstraints(): Constraint[] {
        return [
            {
                type: 'LEVEL_BASED',
                targetId: this.options.baseLevelId,
                data: {
                    constraintType: 'Base',
                    offset: this.options.baseOffset
                }
            },
            {
                type: 'LEVEL_BASED',
                targetId: this.options.topLevelId,
                data: {
                    constraintType: 'Top',
                    offset: this.options.topOffset
                }
            }
        ];
    }

    private detectAttachments(column: EnhancedColumnElement): void {
        if (!this.context) return;

        const tolerance = 0.1; // 100mm
        const basePoint = new THREE.Vector3(...column.geometryData.basePoint);
        const topPoint = new THREE.Vector3(...column.geometryData.topPoint);

        this.context.bimStore.project.elements.forEach((element, id) => {
            if (id === column.id) return;

            const geom = element.geometryData as any;
            if (!geom) return;

            // Check for beam connections at top
            if (element.category === 'Structural Framing' && geom.start && geom.end) {
                const beamStart = new THREE.Vector3(...geom.start);
                const beamEnd = new THREE.Vector3(...geom.end);

                if (topPoint.distanceTo(beamStart) < tolerance || topPoint.distanceTo(beamEnd) < tolerance) {
                    column.geometryData.topAttachment = {
                        type: 'Beam',
                        elementId: id,
                        cutColumn: false
                    };
                    column.connections.top = {
                        type: 'Beam-Column',
                        connectedElementIds: [id]
                    };
                }
            }

            // Check for floor connections
            if (element.category === 'Floors' && geom.boundary) {
                // Simplified: check if column base/top is at floor level
                const floorZ = geom.heightOffsetFromLevel || 0;
                if (Math.abs(basePoint.z - floorZ) < tolerance) {
                    column.geometryData.baseAttachment = {
                        type: 'Floor',
                        elementId: id,
                        cutColumn: false
                    };
                }
            }
        });
    }
}

// ==================== Helper Functions ====================

/**
 * Get available column types
 */
export function getAvailableColumnTypes(): { family: string; type: string; profile: ColumnProfile }[] {
    return Object.entries(STANDARD_COLUMN_PROFILES).map(([key, profile]) => {
        const parts = key.split(': ');
        return { family: parts[0], type: parts[1], profile };
    });
}

/**
 * Create column with options (for external use)
 */
export function createEnhancedColumn(
    basePoint: THREE.Vector3,
    topPoint: THREE.Vector3,
    options: Partial<EnhancedColumnToolOptions>
): EnhancedColumnElement {
    const tool = new ColumnTool();
    tool.setOptions(options);
    return (tool as any).createColumn(basePoint, topPoint);
}

// Export types
